import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";
import userServices from "services/userServices";
import SimpleHeader from "components/Headers/SimpleHeader";
import constants from "constants";
import helpers from "utils/helpers";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  Spinner,
  Alert,
  FormGroup,
  Input,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import ShieldIcon from "@mui/icons-material/Shield";
import SendIcon from "@mui/icons-material/Send";

function VerificationDetailsPage() {
  const { flagId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { notify } = useNotification();

  const [flag, setFlag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Modals / Action states

  const [messageText, setMessageText] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Document preview modals
  const [selfiePreviewOpen, setSelfiePreviewOpen] = useState(false);
  const [docPreviewOpen, setDocPreviewOpen] = useState(false);

  const fetchFlagDetails = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await userServices.getFlagDetails(flagId);
      if (res && res.success) {
        setFlag(res.data);
      } else {
        setError(res.error || t("page.flagged.toast.fetchError", "Failed to fetch flag details."));
      }
    } catch (err) {
      console.error(err);
      setError(t("page.flagged.toast.genericError", "Failed to load details."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFlagDetails();
    // eslint-disable-next-line
  }, [flagId]);

  const handleSendMessage = async () => {
    if (messageText.trim().length === 0) return;
    try {
      setActionLoading(true);
      if (!flag || !flag._id) {
        notify("error", t("categories.moderation.verification.noFlagId", "Target flag ID not found"));
        return;
      }
      
      const res = await userServices.saveGuidance(flag._id, messageText.trim());
      if (res && res.success) {
        notify("success", t("categories.moderation.verification.messageSent", "Suggestion/Guidance message sent successfully"));
        setMessageText("");
        fetchFlagDetails();
      } else {
        notify("error", res?.error || t("page.flagged.toast.statusUpdateFailed", "Failed to save suggestion"));
      }
    } catch (err) {
      console.error(err);
      notify("error", t("page.flagged.toast.genericError", "Failed to send message"));
    } finally {
      setActionLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const options = { day: "2-digit", month: "short", year: "numeric" };
    const dateFormatted = d.toLocaleDateString("en-GB", options);
    const timeFormatted = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
    return `${dateFormatted}, ${timeFormatted}`;
  };

  if (loading) {
    return (
      <div className="text-center py-5" style={{ minHeight: "300px", marginTop: "100px" }}>
        <Spinner color="primary" />
        <p className="mt-2 text-muted">{t("common.loading", "Loading verification details...")}</p>
      </div>
    );
  }

  if (error || !flag) {
    return (
      <Container className="mt-5">
        <Alert color="danger" className="p-4">
          <h4 className="alert-heading">{t("page.flagged.errors.loadFailed", "Error Loading Details")}</h4>
          <p>{error || t("page.flagged.errors.notFound", "Flag not found.")}</p>
          <hr />
          <Button color="secondary" onClick={() => navigate("/admin/moderation/verification")}>
            {t("categories.moderation.verification.backToQueue", "Back to Verification Queue")}
          </Button>
        </Alert>
      </Container>
    );
  }

  const userObj = flag.userId || {};
  const userName = `${userObj?.firstName ?? ""} ${userObj?.lastName ?? ""}`.trim() || t("categories.moderation.unknown", "Unknown User");
  const isSuspended = userObj?.accountStatus === "suspended";
  const userInitials = userName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "US";

  // Formatted registration date
  const registrationDate = userObj?.createdAt 
    ? new Date(userObj.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : "-";

  return (
    <>
      <SimpleHeader
        name={t("categories.moderation.verificationDetails", "Verification Details")}
        parentName={t("sideNav.verificationQueue", "Verification Queue")}
      />

      <Container className="mt--6" fluid>
        {/* Top Profile Info Header Card */}
        <Row className="mb--2">
          <Col xs="12">
            <Card className="shadow-sm border-0 rounded-lg">
              <CardBody className="py-4 px-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                <div className="d-flex align-items-center mb-3 mb-md-0">
                  {/* Custom Blue Avatar */}
                  <div 
                    className="d-flex align-items-center justify-content-center rounded-circle text-primary font-weight-bold mr-3"
                    style={{ 
                      width: "60px", 
                      height: "60px", 
                      backgroundColor: "#EFF6FF", 
                      fontSize: "20px",
                      border: "1px solid #DBEAFE"
                    }}
                  >
                    {userInitials}
                  </div>
                  <div>
                    <h2 className="mb-0 text-slate-800 font-weight-bold" style={{ fontSize: "20px" }}>{userName}</h2>
                    <span className="text-muted text-sm">{userObj?.email ?? ""}</span>
                  </div>

                  <div className="border-left pl-4 ml-4 d-none d-lg-block">
                    <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider">{t("table.headers.memberSince", "Member Since")}</span>
                    <span className="font-weight-bold text-slate-800 text-sm">{registrationDate}</span>
                  </div>

                  <div className="border-left pl-4 ml-4 d-none d-lg-block">
                    <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider">{t("table.headers.phone", "Phone")}</span>
                    <span className="font-weight-bold text-slate-800 text-sm">{userObj.phoneNumber || t("categories.moderation.verification.notProvided", "Not provided")}</span>
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <Badge 
                    className={`badge-pill px-3 py-2 text-uppercase font-weight-700 shadow-sm ${isSuspended ? "status-error" : "status-success"}`}
                  >
                    {t("categories.moderation.verification.account", "Account Status:")} {t("page.user-profile.status." + (userObj.accountStatus || "active"), userObj.accountStatus || "active")}
                  </Badge>
                  <Badge
                    className={`badge-pill px-3 py-2 text-uppercase font-weight-700 ml-md-2 shadow-sm ${userObj.verificationStatus === "approved" ? "status-success" : userObj.verificationStatus === "rejected" ? "status-error" : "status-warning"}`}
                  >
                    {t("categories.moderation.verification.kyc", "KYC Status:")} {t("page.user-profile.status." + (userObj.verificationStatus || "pending"), userObj.verificationStatus || "pending")}
                  </Badge>
                  <Badge 
                    className="badge-pill px-3 py-2 text-uppercase font-weight-700 ml-md-2 shadow-sm status-info"
                  >
                    {t("categories.moderation.verification.flagStatus", "Flag Status:")} {t("page.flagged.status." + flag.status, flag.status)}
                  </Badge>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          {/* Left Column: Documents & Timeline logs */}
          <Col lg="8" xs="12">
            {/* Current Flag & Rejection Details Card */}
            {(flag.context || flag.fixDeadline) && (
              <Card className="shadow-sm border-0 rounded-lg mb-4">
                <CardHeader className="bg-white border-bottom py-3">
                  <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                    <i className="fa fa-exclamation-triangle mr-2 text-warning" />
                    {t("categories.moderation.verification.currentDetails", "Current Flag & Rejection Details")}
                  </h4>
                </CardHeader>
                <CardBody className="p-4">
                  <Row>
                    {flag.context && (
                      <Col md={flag.fixDeadline ? "6" : "12"} xs="12" className="mb-3 mb-md-0">
                        <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider">{t("categories.moderation.verification.rejectionReasons", "Rejection Reason(s)")}</span>
                        <ul className="mt-1 mb-0" style={{ paddingLeft: "16px", listStyleType: "disc" }}>
                          {helpers.formatRejectionReasons(flag.context, t).split("; ").map((reason, i) => (
                            <li key={i} className="text-sm text-slate-800 font-weight-500" style={{ lineHeight: 1.6 }}>{reason}</li>
                          ))}
                        </ul>
                      </Col>
                    )}
                    {flag.fixDeadline && (
                      <Col md={flag.context ? "6" : "12"} xs="12">
                        <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider">{t("table.headers.fixDeadline", "Fix Deadline")}</span>
                        <div className="mt-1 text-sm text-slate-800 font-weight-500">
                          <span className="text-danger">
                            <i className="far fa-calendar-alt mr-1" />
                            {helpers.getFormattedDate(flag.fixDeadline, t)}
                          </span>
                        </div>
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>
            )}

            {/* Timeline / Flag Activity Card */}
            <Card className="shadow-sm border-0 rounded-lg mb-4">
              <CardHeader className="bg-white border-bottom py-3">
                <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                  <i className="fa fa-history mr-2 text-primary" />
                  {t("categories.moderation.verification.flagActivity", "Flag Activity & Audit History")}
                </h4>
              </CardHeader>
              <CardBody className="p-0">
                <div className="table-responsive">
                  <Table className="table align-items-center table-flush mb-0" size="sm">
                    <thead className="thead-light">
                      <tr>
                        <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.actor", "Actor")}</th>
                        <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.action", "Action")}</th>
                        <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.remarks", "Details / Remarks")}</th>
                        <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.dateTime", "Date & Time")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!flag.timeline || flag.timeline.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center py-4 text-muted text-sm italic">
                            {t("page.flagged.noTimeline", "No logs found.")}
                          </td>
                        </tr>
                      ) : (
                        flag.timeline.map((log) => {
                          const actorName = log.actorId
                            ? `${log.actorId.firstName || ""} ${log.actorId.lastName || ""}`.trim() || log.actorId.email
                            : t("categories.moderation.system", "System");
                          const actorRole = log.actorId?.role?.name || t("categories.moderation.system", "System");
                          return (
                            <tr key={log._id}>
                              <td>
                                <span className="font-weight-bold d-block text-slate-800 text-sm">{actorName}</span>
                                <small className="text-muted text-[10px]">{actorRole}</small>
                              </td>
                              <td>
                                <Badge 
                                  style={{ 
                                    backgroundColor: log.action === "created" ? "#EFF6FF" : "#FEF3C7", 
                                    color: log.action === "created" ? "#2563EB" : "#D97706",
                                    fontSize: "10px",
                                    border: `1px solid ${log.action === "created" ? "#BFDBFE" : "#FDE68A"}`
                                  }}
                                  className="text-uppercase"
                                >
                                  {log.action === "created" ? "CREATED" : "STATUS_CHANGED"}
                                </Badge>
                              </td>
                              <td className="text-wrap text-sm text-slate-700" style={{ whiteSpace: "normal", maxWidth: "300px" }}>
                                {helpers.formatNotesDates(log.notes) || "-"}
                              </td>
                              <td>
                                <small className="text-slate-500 font-weight-500">
                                  {formatDateTime(log.timestamp)}
                                </small>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Right Column: Actions & History */}
          <Col lg="4" xs="12">
            {/* Admin Actions Panel */}
            <Card className="shadow-sm border-0 rounded-lg mb-4">
              <CardHeader className="bg-white border-bottom py-3">
                <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                  <ShieldIcon className="text-primary mr-2" style={{ fontSize: "20px" }} />
                  {t("categories.moderation.verification.adminActions", "Admin Actions")}
                </h4>
              </CardHeader>
              <CardBody className="p-4">
                <div className="d-flex flex-column gap-3">
                  {/* Send Guidance / Suggestion Form */}
                  <h5 className="font-weight-bold text-slate-800 mb-2 text-sm">{t("categories.moderation.verification.sendGuidance", "Send Guidance / Suggestion")}</h5>
                  <FormGroup className="mb-3">
                    <Input
                      type="textarea"
                      placeholder={t("categories.moderation.verification.guidancePlaceholder", "Provide clear instructions or request specific changes...")}
                      rows="4"
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      className="border rounded-lg p-2.5 text-sm"
                    />
                  </FormGroup>
                  <Button
                    onClick={handleSendMessage}
                    disabled={messageText.trim().length === 0 || actionLoading}
                    style={{ backgroundColor: "#5B57D6", border: "none" }}
                    className="font-weight-700 d-flex align-items-center justify-content-center py-2.5 text-white rounded-lg shadow-sm"
                  >
                    <SendIcon className="mr-2" style={{ fontSize: "16px" }} />
                    {t("categories.moderation.verification.sendGuidanceButton", "Send Guidance Message")}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Selfie Preview Modal */}
      <Modal isOpen={selfiePreviewOpen} toggle={() => setSelfiePreviewOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setSelfiePreviewOpen(false)} className="border">
          {t("categories.moderation.verification.selfieCapture", "Selfie Image Capture")}
        </ModalHeader>
        <ModalBody className="p-4 text-center bg-light">
          <img 
            src={`${constants.s3Bucket}/face-images/${userObj.faceImages?.[0]}`} 
            alt={t("categories.moderation.verification.selfieImage", "Selfie Image")} 
            className="img-fluid rounded border shadow-sm"
            style={{ maxHeight: "500px", objectFit: "contain" }}
          />
        </ModalBody>
      </Modal>

      {/* Primary ID Document Preview Modal */}
      <Modal isOpen={docPreviewOpen} toggle={() => setDocPreviewOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setDocPreviewOpen(false)} className="border">
          {t("categories.moderation.verification.primaryDocCapture", "Primary Identity Document Capture")}
        </ModalHeader>
        <ModalBody className="p-4 text-center bg-light">
          <img 
            src={`${constants.s3Bucket}/identity-docs/${userObj.identityDocument}`} 
            alt={t("categories.moderation.verification.primaryDoc", "Primary Identity Document")}
            className="img-fluid rounded border shadow-sm"
            style={{ maxHeight: "500px", objectFit: "contain" }}
          />
        </ModalBody>
      </Modal>


    </>
  );
}

export default VerificationDetailsPage;
